/**
 * Brazilian CPF (Cadastro de Pessoas Físicas) validator.
 *
 * Mirrors the backend `CPFValidator` (Swift, MeiucaFoundation) so that
 * form-side validation matches what the API will accept. Pure functions,
 * no I/O — meant for instant feedback in the UI.
 *
 * Accepts strings with or without the canonical mask (`000.000.000-00`):
 * any non-digit is stripped before validation.
 *
 * Rules enforced (in order):
 *   1. After stripping, the string must have exactly 11 digits.
 *   2. CPFs with all-identical digits (`00000000000`, `11111111111`, …)
 *      are rejected — they pass the math but are blacklisted by Receita
 *      Federal and commonly used as placeholders.
 *   3. The two check digits (positions 10 and 11) must match the values
 *      produced by the modulo-11 algorithm.
 */

export function isValidCPF(cpf: string): boolean {
  const digits = cpf.replace(/\D/g, '')
  if (digits.length !== 11) return false

  // All-equal sequences fail by spec.
  if (/^(\d)\1+$/.test(digits)) return false

  const numbers = digits.split('').map((d) => Number.parseInt(d, 10))

  const firstCheck = checkDigit(numbers, 9)
  if (firstCheck !== numbers[9]) return false

  const secondCheck = checkDigit(numbers, 10)
  if (secondCheck !== numbers[10]) return false

  return true
}

export function cpfDigitsOnly(cpf: string): string {
  return cpf.replace(/\D/g, '')
}

/**
 * Modulo-11 check digit for the first `length` digits of `numbers`.
 * Weights start at `length + 1` and decrease by 1 per position.
 */
function checkDigit(numbers: number[], length: number): number {
  let sum = 0
  for (let i = 0; i < length; i++) {
    sum += numbers[i] * (length + 1 - i)
  }
  const remainder = (sum * 10) % 11
  return remainder === 10 ? 0 : remainder
}
